/**
 * View helper for search-related experiments to consolidate and document required client-side functionality.
 */

import ChairishUri from 'chairisher/util/uri';

import { getAmplitudeExperimentFlagMap, shouldExposeExperiment } from 'chairisher/context/experiments';
import { isAsyncSearch } from 'chairisher/context/search';
import { getExperimentVariantValue } from 'chairisher/thirdparty/experiments';

/**
 * @enum {string}
 */
const SearchExperimentParams = {
    BUCKET: 'bucket',
};

/**
 * Creates and returns a data object specific to search-related experiments
 * The bucket parameter is needed to ensure appropriate caching for each variant
 *
 * @returns {Object} The data object with the bucket param and variant value, if applicable
 */
export function getSearchExperimentDataObj() {
    const dataObj = {};
    const popScoreV2Flag = getAmplitudeExperimentFlagMap().POP_SCORE_V2;
    if (isAsyncSearch() && shouldExposeExperiment(popScoreV2Flag)) {
        const value = getExperimentVariantValue(popScoreV2Flag);
        if (value) {
            dataObj[SearchExperimentParams.BUCKET] = value;
        }
    }

    return dataObj;
}

/**
 * Strips search-related experiment parameters from the provided link elements
 *
 * @param {Array.<HTMLLinkElement>} links The link elements containing the params to be stripped
 */
export function stripSearchExperimentParamsFromLinks(links) {
    links.forEach((link) => {
        const href = link.getAttribute('href');

        const filteredParams = ChairishUri.getParamMaps(href).filter(
            (param) => !Object.values(SearchExperimentParams).includes(param.name),
        );

        const newQueryString = filteredParams
            .map((param) => `${ChairishUri.encodeGetParam(param.name)}=${ChairishUri.encodeGetParam(param.value)}`)
            .join('&');

        link.setAttribute('href', href.split('?')[0] + (newQueryString ? `?${newQueryString}` : ''));
    });
}
